import React, { useMemo, useState } from "react";
import { fallbackThumbnail, fetchOEmbed, parseYouTubeId } from "../lib/youtube";
import { UserVideo } from "../lib/library-store";

interface Props {
  onAdd: (video: UserVideo) => Promise<void> | void;
  onTranscribe?: (video: UserVideo) => Promise<void> | void;
  /** When true, skip client-side oEmbed — server fetches it. */
  skipClientOEmbed?: boolean;
}

export default function AddVideoForm({ onAdd, onTranscribe, skipClientOEmbed }: Props) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedUrl = url.trim();
  const parsedId = useMemo(() => (trimmedUrl ? parseYouTubeId(trimmedUrl) : null), [trimmedUrl]);
  const showInvalidHint = trimmedUrl.length > 0 && !parsedId;
  const submitDisabled = busy || !parsedId;

  async function submit(intent: "bookmark" | "transcribe") {
    setError(null);
    const id = parsedId;
    if (!id) {
      setError("That doesn't look like a YouTube URL or video ID.");
      return;
    }
    setBusy(true);
    try {
      const meta = skipClientOEmbed ? null : await fetchOEmbed(id);
      const video: UserVideo = {
        youtubeVideoId: id,
        title: meta?.title ?? "Untitled video",
        channel: meta?.channel ?? "Unknown channel",
        thumbnailUrl: meta?.thumbnailUrl ?? fallbackThumbnail(id),
        status: intent === "transcribe" ? "transcribing" : "bookmarked",
        savedAt: Date.now(),
      };
      if (intent === "transcribe" && onTranscribe) {
        await onTranscribe(video);
      } else {
        await onAdd(video);
      }
      setUrl("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="bg-gray-900 border border-gray-800 rounded-lg p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit("bookmark");
      }}
    >
      <label htmlFor="url" className="block text-sm text-gray-400 mb-2">
        Paste a YouTube URL
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          id="url"
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          disabled={busy}
          className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={submitDisabled}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded transition"
        >
          {busy ? "Adding..." : "Bookmark"}
        </button>
        <button
          type="button"
          onClick={() => void submit("transcribe")}
          disabled={submitDisabled || !onTranscribe}
          title={onTranscribe ? "Extract practice objectives from this video" : "API URL is not configured"}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white rounded transition"
        >
          {busy ? "Working..." : "Transcribe now"}
        </button>
      </div>
      {showInvalidHint && !error && (
        <p className="mt-2 text-sm text-amber-400">
          That doesn&apos;t look like a YouTube URL or video ID.
        </p>
      )}
      {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
    </form>
  );
}
